/**
 * Style Explorer v2 (H-2026-09-12-127) — 3x3 style grid for the create dialog.
 *
 * Flow: the Styles entry→ one BFL flux-2-klein sheet (this file composes the
 * prompt; /api/customer/styles/grid dispatches + owns LoRA routing) → canvas
 * split into 9 labelled cells → pick a cell → /styles/refine restyle → the
 * refined image lands in the C result slot (creation.result), where the
 * existing mockup preview +make-productfunnel take over.
 *
 * Pure parts (composeStyleGrid / pickGridCells / rerollExploreCells) are
 * exported for unit tests; the UI + API parts sit below them.
 */

import { store } from '../../../core/store.js'
import { CONFIG } from '../../../core/config.js'
import { apiRequest, REQUEST_TIMEOUT_MS } from '../../../api/index.js'
import { showToast } from '../../../utils/toast.js'
import { loadImage } from '../../../utils/mockup-composite.js'
import {
  floodFillTransparent,
  isWhiteCornerBackground,
} from '@mamamiya/platform/image-transparency.js'
import { showResultImage, setResultLoading, resetResultSlot } from './result-panel.js'
import { escAttr, escHtml } from './shell-scaffold.js'
import { createLogger } from '../../../utils/logger.js'
const log = createLogger('create.dialog.style-grid')

// ---- Constants ----

export const GRID_COLS = 3
export const GRID_CELLS = 9
export const RESIDENT_SLOTS = 6
export const EXPLORE_SLOTS = 3
/** Anti-abuse: reroll is a paid $0.024 round, capped per session (card B2). */
export const MAX_REROLLS_PER_SESSION = 5
const REROLL_KEY = 'style_grid_rerolls'

// ---- Pure: prompt + slot allocation ----

/**
 * Compose the v2 3x3 grid prompt. Cells are in slot order (0..8, row-major).
 * @param {Array<{style: {prompt_fragment: string}}>} cells
 * @param {string} subject - user subject text (image-is-the-prompt vision text)
 * @returns {string} the BFL prompt
 */
export function composeStyleGrid(cells, subject) {
  if (!Array.isArray(cells) || cells.length !== GRID_CELLS) {
    throw new Error(`composeStyleGrid needs exactly ${GRID_CELLS} cells`)
  }
  const frag = (i) => cells[i].style.prompt_fragment
  return `Design exploration sheet, 3x3 grid layout, bordered panels with clean white
gutters between frames. Each panel: the SAME subject rendered in a DIFFERENT
style, labeled in small text under the panel:
  row1: ${frag(0)} | ${frag(1)} | ${frag(2)}
  row2: ${frag(3)} | ${frag(4)} | ${frag(5)}
  row3: ${frag(6)} | ${frag(7)} | ${frag(8)}
White background. ${subject}`
}

/** Sample n items without replacement (Fisher-Yates via splice). */
function sample(arr, n, rng) {
  const copy = [...arr]
  const out = []
  while (out.length < n && copy.length) {
    out.push(copy.splice(Math.floor(rng() * copy.length), 1)[0])
  }
  return out
}

/**
 * Allocate the 9 grid cells: 6 resident + 3 explore (2:1 — card A3). Explore
 * slots are the bottom row (slots 6-8). A dry pool tops up from the other
 * pool's leftovers so the sheet is always 9 cells when pool >= 9.
 * @param {Array<{id: string, status: string}>} pool - active style_registry rows
 * @param {() => number} [rng] - injectable randomness for tests
 * @returns {Array<{style: object, slot: number, isExplore: boolean}>}
 */
export function pickGridCells(pool, rng = Math.random) {
  const resident = pool.filter((s) => s.status === 'resident')
  const explore = pool.filter((s) => s.status === 'explore')
  let pickedR = sample(resident, RESIDENT_SLOTS, rng)
  let pickedE = sample(explore, EXPLORE_SLOTS, rng)
  if (pickedR.length < RESIDENT_SLOTS) {
    const left = explore.filter((s) => !pickedE.includes(s))
    pickedR = pickedR.concat(sample(left, RESIDENT_SLOTS - pickedR.length, rng))
  }
  if (pickedE.length < EXPLORE_SLOTS) {
    const left = resident.filter((s) => !pickedR.includes(s))
    pickedE = pickedE.concat(sample(left, EXPLORE_SLOTS - pickedE.length, rng))
  }
  return [...pickedR, ...pickedE].map((style, slot) => ({
    style,
    slot,
    isExplore: slot >= RESIDENT_SLOTS,
  }))
}

/**
 * Shuffle— re-roll ONLY the explore cells; the 6 resident cells stay put
 * (card B2). Current explore styles are excluded while the pool allows.
 * @param {ReturnType<typeof pickGridCells>} cells - current grid
 * @param {Array<object>} pool - active style pool
 * @param {() => number} [rng]
 * @returns {ReturnType<typeof pickGridCells>} new cells array
 */
export function rerollExploreCells(cells, pool, rng = Math.random) {
  const currentIds = new Set(cells.filter((c) => c.isExplore).map((c) => c.style.id))
  const explore = pool.filter((s) => s.status === 'explore')
  const fresh = explore.filter((s) => !currentIds.has(s.id))
  let picked = sample(fresh, EXPLORE_SLOTS, rng)
  if (picked.length < EXPLORE_SLOTS) {
    picked = picked.concat(sample(explore, EXPLORE_SLOTS - picked.length, rng))
  }
  const queue = [...picked]
  return cells.map((c) => (c.isExplore ? { ...c, style: queue.shift() ?? c.style } : c))
}

// ---- Session reroll cap ----

export function getRerollsUsed() {
  return Number(sessionStorage.getItem(REROLL_KEY) || '0')
}

export function canReroll() {
  return getRerollsUsed() < MAX_REROLLS_PER_SESSION
}

function bumpRerolls() {
  sessionStorage.setItem(REROLL_KEY, String(getRerollsUsed() + 1))
}

// ---- Canvas: split the sheet into 9 cell dataURLs ----

/**
 * H-139 transparency pass over a sliced cell's pixels — the SAME shared
 * algorithm the material-pool cron runs (packages/platform
 * image-transparency.js), so client and cron products stay identical.
 * White-corner cells get their border-connected white cleared to alpha=0;
 * patterned cells pass through untouched. Invisible to the user by design.
 * @param {{data: Uint8ClampedArray, width: number, height: number}} imageData
 * @returns {boolean} true when the background was alpha-cleared
 */
export function applyTileTransparency(imageData) {
  const { data, width, height } = imageData
  if (!isWhiteCornerBackground(data, width, height)) return false
  floodFillTransparent(data, width, height)
  return true
}

/**
 * Cut the generated 1:1 sheet into 9 equal cells (row-major), each a
 * transparency-processed PNG dataURL.
 * @param {HTMLImageElement} image - loaded grid sheet
 * @returns {string[]} 9 dataURLs in slot order
 */
export function sliceGridImage(image) {
  const w = image.naturalWidth || image.width
  const h = image.naturalHeight || image.height
  const cw = Math.floor(w / GRID_COLS)
  const ch = Math.floor(h / GRID_COLS)
  const out = []
  for (let row = 0; row < GRID_COLS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      const canvas = document.createElement('canvas')
      canvas.width = cw
      canvas.height = ch
      const ctx = canvas.getContext('2d')
      ctx.drawImage(image, col * cw, row * ch, cw, ch, 0, 0, cw, ch)
      try {
        const imageData = ctx.getImageData(0, 0, cw, ch)
        applyTileTransparency(imageData)
        ctx.putImageData(imageData, 0, 0)
      } catch (err) {
        // Honest-fail: keep the opaque slice rather than losing the cell.
        log.warn('tile transparency skipped:', err.message)
      }
      out.push(canvas.toDataURL('image/png'))
    }
  }
  return out
}

// ---- API ----

async function fetchStylePool() {
  const res = await apiRequest(`${CONFIG.FRONTEND_API}/api/customer/styles`, {
    timeoutMs: REQUEST_TIMEOUT_MS.DEFAULT,
  })
  return res?.data?.styles ?? []
}

async function requestStyleGrid({ prompt, styleIds }) {
  return apiRequest(`${CONFIG.FRONTEND_API}/api/customer/styles/grid`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, style_ids: styleIds }),
    timeoutMs: REQUEST_TIMEOUT_MS.GENERATION,
  })
}

async function requestStyleRefine({ styleId, imageUrl }) {
  return apiRequest(`${CONFIG.FRONTEND_API}/api/customer/styles/refine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ style_id: styleId, image_url: imageUrl }),
    timeoutMs: REQUEST_TIMEOUT_MS.GENERATION,
  })
}

function reportStyleSelect(styleId) {
  apiRequest(`${CONFIG.FRONTEND_API}/api/customer/styles/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ style_id: styleId }),
  }).catch((err) => log.warn('select telemetry failed:', err.message))
}

// ---- UI ----

let _wrap = null
let _state = null // { subject, pool, cells, gridImageUrl, busy }

/**
 * Wire thethe Styles entryentry in the genbar. Called once from initDialogShell.
 * @param {HTMLElement} container - the workspace root (#mode-create host)
 */
export function initStyleGrid(container) {
  _wrap = container.querySelector('#styleGridWrap')
  const btn = container.querySelector('#stylesBtn')
  if (!_wrap || !btn) return
  btn.addEventListener('click', () => { void openGrid() })
}

function subjectText() {
  const note = document.getElementById('promptInput')?.value?.trim()
  // Image-is-the-prompt: with no note the sheet renders "the uploaded subject".
  return note || 'the subject in the source image'
}

async function openGrid() {
  if (_state?.busy) return
  if (!store.getState('auth.token')) {
    showToast('Sign in to explore styles')
    return
  }
  _state = { busy: true, subject: subjectText(), pool: null, cells: null, gridImageUrl: null }
  renderGridLoading('Composing 9 styles…')
  try {
    _state.pool = await fetchStylePool()
    if (_state.pool.length < GRID_CELLS) throw new Error('style pool is not seeded yet')
    _state.cells = pickGridCells(_state.pool)
    await generateAndRender()
  } catch (err) {
    log.warn('style grid failed:', err.message)
    showToast(`Style grid failed: ${err.message}`)
    closeGrid()
  } finally {
    if (_state) _state.busy = false
  }
}

async function generateAndRender() {
  const prompt = composeStyleGrid(_state.cells, _state.subject)
  const res = await requestStyleGrid({
    prompt,
    styleIds: _state.cells.map((c) => c.style.id),
  })
  const imageUrl = res?.data?.image_url
  if (!imageUrl) throw new Error('grid returned no image')
  _state.gridImageUrl = imageUrl
  const sheet = await loadImage(imageUrl)
  const slices = sliceGridImage(sheet)
  renderGrid(slices)
}

async function shuffleExplore() {
  if (!_state || _state.busy) return
  if (!canReroll()) {
    showToast(`Shuffle limit reached (${MAX_REROLLS_PER_SESSION} per session)`)
    return
  }
  _state.busy = true
  bumpRerolls()
  renderGridLoading('Rolling a fresh batch…')
  try {
    _state.cells = rerollExploreCells(_state.cells, _state.pool)
    await generateAndRender()
  } catch (err) {
    log.warn('style reroll failed:', err.message)
    showToast(`Shuffle failed: ${err.message}`)
    renderGrid(null)
  } finally {
    _state.busy = false
  }
}

async function pickCell(cell) {
  if (!_state || _state.busy) return
  _state.busy = true
  reportStyleSelect(cell.style.id)
  closeGrid()
  setResultLoading(`Refining in ${cell.style.label}…`)
  try {
    // Source: the user's image (image-is-the-prompt main line); the grid sheet
    // is the fallback anchor when no source image is present (card C).
    const sourceUrl = store.getState('sourceImage')?.url || _state.gridImageUrl
    const res = await requestStyleRefine({ styleId: cell.style.id, imageUrl: sourceUrl })
    const imageUrl = res?.data?.image_url
    if (!imageUrl) throw new Error('refine returned no image')
    showResultImage(imageUrl)
  } catch (err) {
    log.warn('style refine failed:', err.message)
    showToast(`Refine failed: ${err.message}`)
    resetResultSlot()
  } finally {
    _state.busy = false
  }
}

function renderGridLoading(text) {
  if (!_wrap) return
  _wrap.classList.remove('hidden')
  _wrap.innerHTML = `<div class="style-grid-status">${escHtml(text)}</div>`
}

function renderGrid(slices) {
  if (!_wrap || !_state?.cells) return
  _wrap.classList.remove('hidden')
  const rerollsLeft = MAX_REROLLS_PER_SESSION - getRerollsUsed()
  _wrap.innerHTML = `
    <div class="style-grid" role="group" aria-label="Pick a style">
      ${_state.cells.map((c, i) => `
        <button type="button" class="style-cell" data-slot="${c.slot}" data-style-id="${escAttr(c.style.id)}"
                title="Refine in ${escAttr(c.style.label)}">
          ${slices?.[i] ? `<img src="${slices[i]}" alt="${escAttr(c.style.label)}" />` : ''}
          <span class="style-cell-label">${escHtml(c.style.label)}</span>
        </button>`).join('')}
    </div>
    <div class="style-grid-bar">
      <button type="button" class="style-grid-shuffle" id="styleGridShuffle" ${rerollsLeft <= 0 ? 'disabled' : ''}>
        <i class="ph ph-shuffle" aria-hidden="true"></i>Shuffle (${rerollsLeft} left)
      </button>
      <button type="button" class="style-grid-close" id="styleGridClose"><i class="ph ph-x" aria-hidden="true"></i></button>
    </div>`
  _wrap.querySelectorAll('.style-cell').forEach((el) => {
    el.addEventListener('click', () => {
      const cell = _state?.cells?.[Number(el.dataset.slot)]
      if (cell) void pickCell(cell)
    })
  })
  _wrap.querySelector('#styleGridShuffle')?.addEventListener('click', () => { void shuffleExplore() })
  _wrap.querySelector('#styleGridClose')?.addEventListener('click', closeGrid)
}

export function closeGrid() {
  if (_wrap) {
    _wrap.classList.add('hidden')
    _wrap.innerHTML = ''
  }
  if (_state) _state.busy = false
}
